import { createContext, useContext, useEffect, useState } from "react";
import { setOverrideWords } from "../library";
import getUsuarioPermisos from "../services/permisos/getUsuarioPermisos";
import getConfiguracionCliente from "../services/configuracionCliente/getConfiguracionCliente";
import fallbackWords from "../data/words.json";

const WordsContext = createContext(null);

const DEFAULT_THEME = {
  brand: {
    default: "#E86926",
    dark: "#cc5a12",
    accent: "#E66A20",
    light: "#FFF4EC",
  },
  buttonGray: "#656565",
  grayLight: "#E9ECF1",
  grayCustom: "#4D4D4D70",
  navy: {
    default: "#1E3A8A",
    dark: "#162e6e",
  },
  signature: {
    default: "#EAD6C1",
    dark: "#e1c5a9",
  },
};

function hexToRgbParts(hex) {
  if (typeof hex !== "string") return null;
  const value = hex.trim().replace(/^#/, "");
  if (![6, 8].includes(value.length)) return null;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function hexToAlpha(hex) {
  if (typeof hex !== "string") return null;
  const value = hex.trim().replace(/^#/, "");
  if (value.length !== 8) return null;
  const a = parseInt(value.slice(6, 8), 16);
  if (Number.isNaN(a)) return null;
  return a / 255;
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;

  const t = theme && typeof theme === "object" ? theme : {};
  const get = (path, fallback) =>
    path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), t) ??
    fallback;

  const brandDefault = get(["brand", "default"], DEFAULT_THEME.brand.default);
  const brandDark = get(["brand", "dark"], DEFAULT_THEME.brand.dark);
  const brandAccent = get(["brand", "accent"], DEFAULT_THEME.brand.accent);
  const brandLight = get(["brand", "light"], DEFAULT_THEME.brand.light);

  const buttonGray = t.buttonGray ?? DEFAULT_THEME.buttonGray;
  const grayLight = t.grayLight ?? DEFAULT_THEME.grayLight;
  const grayCustom = t.grayCustom ?? DEFAULT_THEME.grayCustom;

  const navyDefault = get(["navy", "default"], DEFAULT_THEME.navy.default);
  const navyDark = get(["navy", "dark"], DEFAULT_THEME.navy.dark);

  const signatureDefault = get(["signature", "default"], DEFAULT_THEME.signature.default);
  const signatureDark = get(["signature", "dark"], DEFAULT_THEME.signature.dark);

  const setRgbVar = (varName, hexValue) => {
    const rgb = hexToRgbParts(hexValue);
    if (!rgb) return;
    document.documentElement.style.setProperty(varName, `${rgb.r} ${rgb.g} ${rgb.b}`);
  };

  setRgbVar("--brand-rgb", brandDefault);
  setRgbVar("--brand-dark-rgb", brandDark);
  setRgbVar("--brand-accent-rgb", brandAccent);
  setRgbVar("--brand-light-rgb", brandLight);

  setRgbVar("--button-gray-rgb", buttonGray);
  setRgbVar("--gray-light-rgb", grayLight);

  const grayCustomRgb = hexToRgbParts(grayCustom);
  if (grayCustomRgb) {
    document.documentElement.style.setProperty(
      "--gray-custom-rgb",
      `${grayCustomRgb.r} ${grayCustomRgb.g} ${grayCustomRgb.b}`
    );
  }
  const grayCustomAlpha = hexToAlpha(grayCustom);
  if (grayCustomAlpha !== null) {
    document.documentElement.style.setProperty("--gray-custom-alpha", String(grayCustomAlpha));
  }

  setRgbVar("--navy-rgb", navyDefault);
  setRgbVar("--navy-dark-rgb", navyDark);

  setRgbVar("--signature-rgb", signatureDefault);
  setRgbVar("--signature-dark-rgb", signatureDark);
}

export const WordsProvider = ({ children }) => {
  const [wordsState, setWordsState] = useState(fallbackWords);
  const [version, setVersion] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const inicializar = async () => {
      try {
        applyTheme(DEFAULT_THEME);
        const cacheKey = "wordsConfig";
        let cached = null;

        try {
          const cachedRaw = window.localStorage.getItem(cacheKey);
          if (cachedRaw) {
            cached = JSON.parse(cachedRaw);
          }
        } catch (e) {
          cached = null;
        }

        if (cached && cached.words && typeof cached.words === "object") {
          setWordsState(cached.words);
          setVersion(cached.versionHash || null);
          setTenantId(null);
          setOverrideWords(cached.words);
          if (cached.theme) applyTheme(cached.theme);
        }

        const permisosResponse = await getUsuarioPermisos();
        const payload = permisosResponse?.data;

        if (!payload || !payload.success || !payload.data) {
          setLoading(false);
          return;
        }

        const configuracionCliente = payload.data.configuracionCliente;
        const remoteVersionHash = configuracionCliente?.versionHash || null;

        const cachedVersionHash = cached?.versionHash || null;

        let tenantIdFromStorage = null;
        try {
          const rawUserData = window.localStorage.getItem("potencial-talento-user-data");
          if (rawUserData) {
            const parsedUserData = JSON.parse(rawUserData);
            tenantIdFromStorage =
              parsedUserData?.client_id ||
              parsedUserData?.clientId ||
              parsedUserData?.tenantId ||
              null;
          }
        } catch (e) {
          tenantIdFromStorage = null;
        }

        const configResponse = await getConfiguracionCliente(
          tenantIdFromStorage || "current"
        );
        const configPayload = configResponse?.data;

        if (remoteVersionHash && remoteVersionHash === cachedVersionHash) {
          if (configPayload?.success && configPayload?.data?.theme) {
            applyTheme(configPayload.data.theme);
          }
          setLoading(false);
          return;
        }

        if (!configPayload || !configPayload.success || !configPayload.data) {
          setWordsState(fallbackWords);
          setVersion(null);
          setTenantId(null);
          setOverrideWords(fallbackWords);
          applyTheme(DEFAULT_THEME);

          try {
            window.localStorage.setItem(
              cacheKey,
              JSON.stringify({
                versionHash: null,
                words: fallbackWords,
                theme: DEFAULT_THEME,
              })
            );
          } catch (e) {
            // No romper la app si falla el cache.
          }

          return;
        }

        const config = configPayload.data;
        let parsedWords = fallbackWords;

        if (config.words && typeof config.words === "object") {
          parsedWords = config.words;
        } else if (config.wordsJson) {
          try {
            parsedWords = JSON.parse(config.wordsJson || "{}");
          } catch (e) {
            parsedWords = fallbackWords;
          }
        }

        setWordsState(parsedWords);
        setVersion(config.versionHash || null);
        setTenantId(null);
        setOverrideWords(parsedWords);

        if (config.theme && typeof config.theme === "object") {
          applyTheme(config.theme);
        }

        try {
          window.localStorage.setItem(
            cacheKey,
            JSON.stringify({
              versionHash: config.versionHash || null,
              words: parsedWords,
              theme: config.theme ?? null,
            })
          );
        } catch (e) {
        }
      } finally {
        setLoading(false);
      }
    };

    inicializar();
  }, []);

  return (
    <WordsContext.Provider
      value={{
        words: wordsState,
        version,
        tenantId,
        loading,
      }}
    >
      {children}
    </WordsContext.Provider>
  );
};

export const useWords = () => {
  const context = useContext(WordsContext);
  if (!context) {
    throw new Error("useWords debe ser usado dentro de WordsProvider");
  }
  return context;
};

